import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import BlogCard from '../components/blog/BlogCard';
import { allPosts } from '../data/blog/posts';
import { BlogCategory } from '../data/blog/types';

const categories: BlogCategory[] = ['Lifestyle', 'Travel', 'Community', 'Tips'];

const BlogListingPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<BlogCategory | null>(null);

  const filteredPosts = activeCategory
    ? allPosts.filter((p) => p.categories.includes(activeCategory))
    : allPosts;

  // Blog + ItemList JSON-LD, always built from the full post list (not the active filter)
  const blogSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Blog',
        '@id': 'https://dinksocial.com/blog#blog',
        name: 'Dink Journal',
        url: 'https://dinksocial.com/blog',
        description:
          'Articles, data, and guides for DINK and childfree couples. Stories, lifestyle tips, and community insights.',
        publisher: { '@id': 'https://dinksocial.com/#organization' },
        blogPost: allPosts.map((p) => ({
          '@type': 'BlogPosting',
          headline: p.title,
          url: p.canonicalUrl,
          datePublished: p.publishDate,
          dateModified: p.lastModified,
          author: { '@type': 'Organization', name: p.author },
        })),
      },
      {
        '@type': 'ItemList',
        itemListElement: allPosts.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: p.canonicalUrl,
          name: p.title,
        })),
      },
    ],
  };

  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Dink Journal | DINK Social — Stories for Childfree Couples</title>
        <meta
          name="description"
          content="Articles, data, and guides for DINK and childfree couples. Stories, lifestyle tips, and community insights."
        />
        <meta property="og:title" content="Dink Journal | DINK Social — Stories for Childfree Couples" />
        <meta
          property="og:description"
          content="Articles, data, and guides for DINK and childfree couples. Stories, lifestyle tips, and community insights."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dinksocial.com/blog" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dink Journal | DINK Social — Stories for Childfree Couples" />
        <meta name="twitter:description" content="Articles, data, and guides for DINK and childfree couples. Stories, lifestyle tips, and community insights." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/blog" />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <Nav />

      {/* ─── HEADING — editorial, white ground ─────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Dink Journal
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px] max-w-2xl"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            Stories, tips, and inspiration for childfree couples
          </p>
        </div>
      </section>

      {/* ─── CATEGORY PILLS ────────────────────────────────── */}
      <section className="px-6 md:px-10">
        <div className="max-w-6xl mx-auto pb-2 border-b border-ink/10">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-5 py-2 rounded-full text-[13px] font-medium transition-colors ${
                activeCategory === null
                  ? 'bg-primary text-white'
                  : 'border border-ink/15 text-ink/70 hover:border-ink/30 hover:text-ink'
              }`}
            >
              All posts
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-[13px] font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-primary text-white'
                    : 'border border-ink/15 text-ink/70 hover:border-ink/30 hover:text-ink'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── POST GRID ─────────────────────────────────────── */}
      <section className="py-12 md:py-20 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          {filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
              {filteredPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[17px] text-ink/60">
                No posts in this category yet. Check back soon
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListingPage;
